import Icon from '@ant-design/icons';
import { Divider } from 'antd';
import classNames from 'classnames';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

// import LockSvg from '@/components/Icon/svgs/lock_fill.svg';
import DiscordSvg from '@/components/Icon/svgs/menu_discord.svg';
import DocumentSvg from '@/components/Icon/svgs/menu_docs.svg';
import MenuSvg from '@/components/Icon/svgs/menu_outline.svg';
// import AvatarSvg from '@/components/Icon/svgs/menu_profile.svg';
import PAPopover from '@/components/popover';
import { useModel } from '@/hooks/useModel';
import useStorage from '@/hooks/useStorage';
import useSubscription from '@/hooks/useSubscription';
import { useTranslation } from '@/i18n/client';
import useReferral from '@/store/useReferralStore';
import { EVENT } from '@/utils/event';
import { isTrulyProd } from '@/utils/host';
import { ROUTER_PATH, prefetch } from '@/utils/router';

import { LayoutSecondaryPanelEnum } from '../../constants';
import CreditUsage from './components/CreditUsage';
import Login from './components/Login';
import AppLanguageItem from './components/MenuItem/AppLanguageItem';
import PreferredLanguageItem from './components/MenuItem/PreferedLanguageItem';
import ProfileItem from './components/MenuItem/ProfileItem';
import Referral from './components/MenuItem/Referral';
import SubscriptionItem from './components/MenuItem/SubscriptionItem';
import TryNewFeatureItem from './components/MenuItem/TryNewFeatureItem';
// import SupportAccess from './components/SupportAccess';
import { IconMap, MenuItem, MenuTNameMap } from './components/constant';
import styles from './index.less';

interface IProps {
  className?: string;
}

function SystemMenu(props: IProps): JSX.Element | null {
  const { className } = props;
  const [open, setOpen] = useState<boolean>(false);
  const { isLogin, userProfile } = useModel('auth-model', (model) => ({
    isLogin: model.isLogin,
    userProfile: model.userProfile
  }));
  // const expandTimer = useRef<NodeJS.Timeout | null>(null);
  const router = useRouter();
  const [layoutSecondaryPanel, setLayoutSecondaryPanel] =
    useStorage<string>('layoutSecondaryPanel');
  const { updateUsage, updatePriceList } = useSubscription((state) => ({
    updateUsage: state.updateUsage,
    updatePriceList: state.updatePriceList
  }));

  const { t } = useTranslation();

  useEffect(() => {
    const handleUnfoldMenu = () => {
      setOpen(true);
    };

    const handleFoldMenu = () => {
      setOpen(false);
    };

    addEventListener(EVENT.UNFOLD_MENU, handleUnfoldMenu);
    addEventListener(EVENT.FOLD_MENU, handleFoldMenu);

    return () => {
      removeEventListener(EVENT.UNFOLD_MENU, handleUnfoldMenu);
      removeEventListener(EVENT.FOLD_MENU, handleFoldMenu);
    };
  }, []);

  useEffect(() => {
    if (isLogin) {
      updateUsage();
      updatePriceList();
    }

    addEventListener(EVENT.UPDATE_SUBSCRIPTION, updateUsage);

    return () => {
      removeEventListener(EVENT.UPDATE_SUBSCRIPTION, updateUsage);
    };
  }, [isLogin]);

  useEffect(() => {
    if (open) {
      useReferral.getState().getRewardDetail();
      prefetch(router, ROUTER_PATH.login);
    }
  }, [open]);

  const renderMenu = useCallback(() => {
    return (
      <div className={styles.menuContainer}>
        {/* TopContainer */}
        {isLogin && (
          <>
            <div
              className={classNames(styles.menuItem, {
                [styles.aboutMe]: true
              })}
              onClick={() => {
                setLayoutSecondaryPanel((v) => {
                  if (v === LayoutSecondaryPanelEnum.brainMap) {
                    return '';
                  }

                  return LayoutSecondaryPanelEnum.brainMap;
                });
                dispatchEvent(new Event(EVENT.FOLD_MENU));
              }}
            >
              <Icon className={styles.icon} component={IconMap[MenuItem.AboutMe]} />
              <span className={styles.text}>{t(MenuTNameMap[MenuItem.AboutMe])}</span>
            </div>

            <Divider className={styles.divider} />

            <ProfileItem />

            <AppLanguageItem />

            <PreferredLanguageItem />

            <SubscriptionItem />

            <CreditUsage />

            <Divider className={styles.divider} />
          </>
        )}

        {/* Referral */}
        {isLogin ? <Referral /> : null}

        {/* Document */}
        <div
          className={styles.menuItem}
          onClick={() => {
            window.open('https://docs.me.bot/');
          }}
        >
          <Icon className={styles.icon} component={DocumentSvg} />
          <span className={styles.text}>{t('menu.documentation')}</span>
        </div>

        {/* Try New Feature (API Keys) */}
        {isLogin && !isTrulyProd() && <TryNewFeatureItem />}

        {/* Discord */}
        <div
          className={styles.menuItem}
          onClick={() => {
            window.open('https://discord.gg/G6EksdTUfb');
          }}
        >
          <Icon className={styles.icon} component={DiscordSvg} />
          <span className={styles.text}>{t('menu.discord')}</span>
        </div>

        {/* Privacy */}
        {/* <div
          className={styles.menuItem}
          onClick={() => {
            window.open('https://docs.me.bot/more/privacy-protection');
          }}
        >
          <Icon className={styles.icon} component={LockSvg} />
          <span className={styles.text}>{t('menu.privacyProtection')}</span>
        </div> */}

        {/* Support Access */}
        {/* {isLogin && <SupportAccess />} */}

        {/* Login */}
        <Divider className={styles.divider} />
        <Login />
      </div>
    );
  }, [isLogin, layoutSecondaryPanel]);

  return (
    <div
      className={classNames(className, styles.menuIconContainer)}
      // onClick={() => {
      //   setOpen(true);
      // }}
      // onMouseEnter={() => {
      //   if (expandTimer.current) {
      //     clearTimeout(expandTimer.current);
      //     expandTimer.current = null;
      //   }
      // }}
      // onMouseLeave={() => {
      //   expandTimer.current = setTimeout(() => {
      //     setOpen(false);
      //   }, 300);
      // }}
    >
      <PAPopover
        arrow={false}
        content={renderMenu()}
        onOpenChange={(v) => {
          setOpen(v);
        }}
        open={open}
        overlayClassName={styles.overlayClassName}
        placement="topRight"
        trigger={'click'}
      >
        {userProfile?.avatar ? (
          <img className={styles.menuIcon} src={userProfile.avatar} />
        ) : (
          <Icon className={styles.menuIcon} component={MenuSvg} />
        )}
      </PAPopover>
    </div>
  );
}

export default SystemMenu;
